import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import {
  Plus, FileText, FileType, Send, Edit, Trash2, Search,
  Lock, Unlock, Building2, Calendar as CalendarIcon, AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PERIOD_TYPES = [
  { value: "weekly",    label: "Weekly" },
  { value: "monthly",   label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual",    label: "Annual" },
];

const SEVERITY_TONE = {
  low: "slate", medium: "amber", high: "red", critical: "red",
};

function makeEmptyReport(branch = "") {
  const today = new Date().toISOString().slice(0, 10);
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return {
    title: "",
    period_type: "weekly",
    period_start: start.toISOString().slice(0, 10),
    period_end: today,
    branch,
    executive_summary: "",
    detailed_description: "",
    issues: [],
    sales_target: 0,
    sales_actual: 0,
    expenditure: 0,
    expenditure_notes: "",
    staff_headcount: 0,
    staff_absences: 0,
    top_selling_products: "",
    customer_feedback_highlights: "",
    achievements: "",
    challenges: "",
    action_items: "",
    next_period_plan: "",
  };
}

function autoRange(periodType) {
  const end = new Date();
  const start = new Date();
  if (periodType === "weekly") start.setDate(end.getDate() - 6);
  else if (periodType === "monthly") start.setMonth(end.getMonth() - 1, end.getDate() + 1);
  else if (periodType === "quarterly") start.setMonth(end.getMonth() - 3, end.getDate() + 1);
  else if (periodType === "annual") start.setFullYear(end.getFullYear() - 1, end.getMonth(), end.getDate() + 1);
  return {
    period_start: start.toISOString().slice(0, 10),
    period_end: end.toISOString().slice(0, 10),
  };
}

function IssueRow({ issue, onChange, onRemove, idx }) {
  const set = (k, v) => onChange({ ...issue, [k]: v });
  return (
    <div className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50/60"
      data-testid={`preport-issue-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
          Issue {idx + 1}
        </span>
        <Button type="button" variant="ghost" size="sm"
          onClick={onRemove} className="h-6 text-red-700"
          data-testid={`preport-issue-remove-${idx}`}>
          <Trash2 size={12} />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Input value={issue.title} onChange={(e) => set("title", e.target.value)}
          placeholder="Title" className="h-8 col-span-3"
          data-testid={`preport-issue-title-${idx}`} />
        <Select value={issue.severity || "medium"}
          onValueChange={(v) => set("severity", v)}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["low", "medium", "high", "critical"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={issue.status || "open"} onValueChange={(v) => set("status", v)}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["open", "resolved", "escalated"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Textarea rows={2} value={issue.description || ""}
        onChange={(e) => set("description", e.target.value)}
        placeholder="Description (optional)" className="text-xs" />
    </div>
  );
}

function Field({ label, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <Label>{label}</Label>
      {hint && <div className="text-[10px] text-gray-500 mb-0.5">{hint}</div>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ReportForm({ open, onOpenChange, initial, onSaved, branches, users }) {
  const [form, setForm] = useState(initial || makeEmptyReport());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial || makeEmptyReport()); }, [open, initial]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e, andSubmitAfter = false) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = {
        ...form,
        sales_target: Number(form.sales_target) || 0,
        sales_actual: Number(form.sales_actual) || 0,
        expenditure: Number(form.expenditure) || 0,
        staff_headcount: parseInt(form.staff_headcount) || 0,
        staff_absences: parseInt(form.staff_absences) || 0,
        issues: (form.issues || []).filter((i) => (i.title || "").trim()),
      };
      let saved;
      if (initial?.id) {
        ({ data: saved } = await api.patch(`/periodic-reports/${initial.id}`, payload));
      } else {
        ({ data: saved } = await api.post("/periodic-reports", payload));
      }
      if (andSubmitAfter) {
        await api.post(`/periodic-reports/${saved.id}/submit`);
        toast.success("Report submitted");
      } else {
        toast.success(initial?.id ? "Report updated" : "Draft saved");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const salesVariance = (Number(form.sales_actual) || 0) - (Number(form.sales_target) || 0);
  const varianceTone = salesVariance >= 0 ? "emerald" : "red";
  const locked = initial?.status === "submitted";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="preport-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileText size={16} />
            {initial?.id ? "Edit periodic report" : "New periodic report"}
            {locked && <Badge tone="slate"><Lock size={10} className="inline mr-1" />Submitted</Badge>}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => submit(e, false)} className="space-y-4" data-testid="preport-form">
          <fieldset disabled={locked} className={locked ? "opacity-60 pointer-events-none" : ""}>
            {/* Header */}
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Report title" testid="preport-title-wrap">
                <Input required value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="e.g. Main Abrah — Weekly Report W27"
                  data-testid="preport-title-input" />
              </Field>
              <Field label="Branch">
                <Select value={form.branch} onValueChange={(v) => setField("branch", v)}>
                  <SelectTrigger data-testid="preport-branch-select">
                    <SelectValue placeholder="Choose branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Period type">
                <Select value={form.period_type} onValueChange={(v) => {
                  const range = autoRange(v);
                  setForm({ ...form, period_type: v, ...range });
                }}>
                  <SelectTrigger data-testid="preport-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERIOD_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Period start">
                  <Input type="date" value={form.period_start}
                    onChange={(e) => setField("period_start", e.target.value)}
                    data-testid="preport-start-date" />
                </Field>
                <Field label="Period end">
                  <Input type="date" value={form.period_end}
                    onChange={(e) => setField("period_end", e.target.value)}
                    data-testid="preport-end-date" />
                </Field>
              </div>
            </div>

            <Field label="Executive summary" hint="One paragraph. What went well, what didn't.">
              <Textarea rows={2} value={form.executive_summary}
                onChange={(e) => setField("executive_summary", e.target.value)}
                data-testid="preport-summary" />
            </Field>

            <Field label="Detailed description">
              <Textarea rows={4} value={form.detailed_description}
                onChange={(e) => setField("detailed_description", e.target.value)}
                data-testid="preport-detailed" />
            </Field>

            {/* Issues */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-widest font-semibold text-emerald-800 flex items-center gap-1">
                  <AlertTriangle size={12} /> Issues raised
                </div>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => setForm({ ...form, issues: [...form.issues, { title: "", severity: "medium", status: "open", description: "" }] })}
                  data-testid="preport-issue-add">
                  <Plus size={12} className="mr-1" /> Add issue
                </Button>
              </div>
              <div className="space-y-2">
                {form.issues.length === 0 && (
                  <div className="text-xs text-gray-500 italic border border-dashed border-gray-200 rounded-md py-4 text-center">
                    No issues added yet.
                  </div>
                )}
                {form.issues.map((it, i) => (
                  <IssueRow key={i} issue={it} idx={i}
                    onChange={(nv) => {
                      const next = [...form.issues]; next[i] = nv; setForm({ ...form, issues: next });
                    }}
                    onRemove={() => setForm({ ...form, issues: form.issues.filter((_, j) => j !== i) })}
                  />
                ))}
              </div>
            </div>

            {/* Financials */}
            <div className="grid md:grid-cols-4 gap-3">
              <Field label="Sales target (UGX)">
                <Input type="number" value={form.sales_target}
                  onChange={(e) => setField("sales_target", e.target.value)}
                  data-testid="preport-sales-target" />
              </Field>
              <Field label="Sales actual (UGX)">
                <Input type="number" value={form.sales_actual}
                  onChange={(e) => setField("sales_actual", e.target.value)}
                  data-testid="preport-sales-actual" />
              </Field>
              <Field label="Variance">
                <div className="h-10 flex items-center px-3 rounded-md border border-gray-200 bg-gray-50">
                  <Badge tone={varianceTone}>
                    {salesVariance >= 0 ? "+" : ""}UGX {Math.abs(salesVariance).toLocaleString()}
                  </Badge>
                </div>
              </Field>
              <Field label="Expenditure (UGX)">
                <Input type="number" value={form.expenditure}
                  onChange={(e) => setField("expenditure", e.target.value)}
                  data-testid="preport-expenditure" />
              </Field>
            </div>

            <Field label="Expenditure notes">
              <Textarea rows={2} value={form.expenditure_notes}
                onChange={(e) => setField("expenditure_notes", e.target.value)} />
            </Field>

            {/* Staff */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Staff headcount">
                <Input type="number" value={form.staff_headcount}
                  onChange={(e) => setField("staff_headcount", e.target.value)} />
              </Field>
              <Field label="Absences">
                <Input type="number" value={form.staff_absences}
                  onChange={(e) => setField("staff_absences", e.target.value)} />
              </Field>
            </div>

            <Field label="Top-selling products"
              hint="One per line, ranked. Numbers help executive review.">
              <Textarea rows={3} value={form.top_selling_products}
                onChange={(e) => setField("top_selling_products", e.target.value)} />
            </Field>
            <Field label="Customer feedback highlights">
              <Textarea rows={2} value={form.customer_feedback_highlights}
                onChange={(e) => setField("customer_feedback_highlights", e.target.value)} />
            </Field>
            <Field label="Achievements & wins">
              <Textarea rows={2} value={form.achievements}
                onChange={(e) => setField("achievements", e.target.value)} />
            </Field>
            <Field label="Challenges">
              <Textarea rows={2} value={form.challenges}
                onChange={(e) => setField("challenges", e.target.value)} />
            </Field>
            <Field label="Action items">
              <Textarea rows={3} value={form.action_items}
                onChange={(e) => setField("action_items", e.target.value)} />
            </Field>
            <Field label="Plan for next period">
              <Textarea rows={2} value={form.next_period_plan}
                onChange={(e) => setField("next_period_plan", e.target.value)} />
            </Field>
          </fieldset>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {!locked && (
              <>
                <Button type="submit" variant="outline" disabled={busy}
                  data-testid="preport-save-draft">
                  {busy ? "Saving…" : (initial?.id ? "Save" : "Save draft")}
                </Button>
                <Button type="button" disabled={busy}
                  onClick={(e) => submit(e, true)}
                  className="bg-emerald-900 hover:bg-emerald-950 text-white"
                  data-testid="preport-save-submit">
                  <Send size={12} className="mr-1.5" /> Save &amp; submit
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PeriodicReports() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({
    branch: "all", period_type: "all", status: "all", q: "",
  });

  const canReopen = ["managing_director", "executive_director"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: b }, { data: u }] = await Promise.all([
        api.get("/periodic-reports"), api.get("/branches"), api.get("/users"),
      ]);
      setItems(r); setBranches(b); setUsers(u);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const filtered = useMemo(() => items.filter((i) => {
    if (filters.branch !== "all" && i.branch !== filters.branch) return false;
    if (filters.period_type !== "all" && i.period_type !== filters.period_type) return false;
    if (filters.status !== "all" && (i.status || "draft") !== filters.status) return false;
    if (filters.q) {
      const q = filters.q.toLowerCase();
      const hay = `${i.title} ${i.branch} ${i.executive_summary} ${i.created_by_name}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [items, filters]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of filtered) {
      const b = r.branch || "Unassigned";
      if (!m.has(b)) m.set(b, []);
      m.get(b).push(r);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const download = async (id, fmt) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/periodic-reports/${id}/export.${fmt}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename="?([^";]+)"?/.exec(cd);
    a.download = m ? m[1] : `report.${fmt}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete '${r.title}'? This cannot be undone.`)) return;
    try { await api.delete(`/periodic-reports/${r.id}`); toast.success("Deleted"); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reopen = async (r) => {
    try { await api.post(`/periodic-reports/${r.id}/reopen`); toast.success("Report reopened"); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Periodic Reports"
        subtitle="Weekly, monthly, quarterly and annual branch reports. Submit for record and download as PDF or Word."
        actions={
          <Button className="bg-emerald-900 hover:bg-emerald-950 text-white"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="preport-new-button">
            <Plus size={16} className="mr-1.5" /> New report
          </Button>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex flex-wrap items-end gap-3"
        data-testid="preport-filters">
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Branch</Label>
          <Select value={filters.branch} onValueChange={(v) => setFilters({ ...filters, branch: v })}>
            <SelectTrigger className="mt-1 h-9 w-44" data-testid="preport-filter-branch"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Period</Label>
          <Select value={filters.period_type} onValueChange={(v) => setFilters({ ...filters, period_type: v })}>
            <SelectTrigger className="mt-1 h-9 w-36" data-testid="preport-filter-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All periods</SelectItem>
              {PERIOD_TYPES.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="mt-1 h-9 w-32" data-testid="preport-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-widest">Search</Label>
          <div className="relative mt-1">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Title, summary, author…" className="pl-8 h-9"
              data-testid="preport-filter-search" />
          </div>
        </div>
      </div>

      {/* Grouped list */}
      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : grouped.length === 0 ? (
        <EmptyState title="No periodic reports yet"
          hint="Create the first weekly / monthly / annual report." />
      ) : (
        <div className="space-y-6" data-testid="preport-list">
          {grouped.map(([branch, entries]) => (
            <div key={branch}>
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={14} className="text-emerald-800" />
                <div className="text-sm font-display font-semibold text-emerald-900">{branch}</div>
                <Badge tone="slate">{entries.length}</Badge>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {entries.map((r) => (
                  <div key={r.id}
                    className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-sm transition-shadow"
                    data-testid={`preport-card-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <Badge tone="emerald">{PERIOD_TYPES.find((p) => p.value === r.period_type)?.label || r.period_type}</Badge>
                      {r.status === "submitted"
                        ? <Badge tone="slate"><Lock size={10} className="inline mr-1" />Submitted</Badge>
                        : <Badge tone="amber">Draft</Badge>}
                    </div>
                    <div className="font-display font-semibold text-gray-900 text-sm leading-snug"
                      data-testid={`preport-card-title-${r.id}`}>{r.title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                      <CalendarIcon size={10} /> {r.period_start} → {r.period_end}
                    </div>
                    {r.executive_summary && (
                      <div className="text-xs text-gray-600 mt-2 line-clamp-2">{r.executive_summary}</div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2 text-[10px]">
                      {(r.issues || []).slice(0, 3).map((i, idx) => (
                        <Badge key={idx} tone={SEVERITY_TONE[i.severity] || "slate"}>
                          {i.severity}
                        </Badge>
                      ))}
                      {(r.issues || []).length > 3 && (
                        <span className="text-gray-400 text-[10px]">+{r.issues.length - 3}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-3 pt-2 border-t border-gray-100">
                      {r.created_by_name || "—"} · {(r.updated_at || r.created_at || "").slice(0, 10)}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-3">
                      <Button size="sm" variant="outline"
                        onClick={() => { setEditing(r); setDialogOpen(true); }}
                        data-testid={`preport-edit-${r.id}`}>
                        <Edit size={12} className="mr-1" />
                        {r.status === "submitted" ? "View" : "Edit"}
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => download(r.id, "pdf")}
                        data-testid={`preport-pdf-${r.id}`}>
                        <FileText size={12} className="mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => download(r.id, "docx")}
                        data-testid={`preport-docx-${r.id}`}>
                        <FileType size={12} className="mr-1" /> Word
                      </Button>
                      {r.status === "submitted" && canReopen && (
                        <Button size="sm" variant="ghost"
                          onClick={() => reopen(r)}
                          className="text-amber-800 hover:bg-amber-50"
                          data-testid={`preport-reopen-${r.id}`}>
                          <Unlock size={12} className="mr-1" /> Reopen
                        </Button>
                      )}
                      <Button size="sm" variant="ghost"
                        onClick={() => remove(r)}
                        className="text-red-700 hover:bg-red-50 ml-auto"
                        data-testid={`preport-delete-${r.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ReportForm
        open={dialogOpen} onOpenChange={setDialogOpen}
        initial={editing}
        branches={branches}
        users={users}
        onSaved={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}
